import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Lock, BookOpen, Eye, Lightbulb, Flame, MessageCircle, Bell,
  AlertTriangle, Loader2, Pause, CheckCircle2,
} from "lucide-react";
import RedesignLayout from "@/components/redesign/RedesignLayout";
import { api } from "@/lib/api";

// Sprint 8.D — Kündigungs-Screen mit Asset-Verlust + "30 Tage Pause"-Angebot.
// Reihenfolge der 6 Verlust-Items wurde mit dem Coach abgestimmt:
//   1. Flexpfad-Bibliothek (Hauptasset)
//   2. Flexpfad-Reise
//   3. Hot Tips
//   4. Streak
//   5. Conversational Trigger
//   6. Wochen- + Monats-Email

const LOSS_ITEMS = [
  {
    icon: BookOpen,
    title: "Deine Flexpfad-Bibliothek",
    detail:
      "Alle gespeicherten Flexpfade — mit Tags, Notizen und Verlauf — werden nach 14 Tagen anonymisiert.",
  },
  {
    icon: Eye,
    title: "Deine Flexpfad-Reise",
    detail:
      "Die Flexpfad-Reise zeigt, in welchen Themen du immer wieder landest. Pause oder Kündigung friert sie ein.",
  },
  {
    icon: Lightbulb,
    title: "Deine 8 wöchentlichen Hot Tips",
    detail:
      "Personalisiert auf dein Top-Thema — basierend auf deinen letzten Flexpfade.",
  },
  {
    icon: Flame,
    title: "Deine Flexpfad-Serie",
    detail:
      "Deine Tages-Routine + die Coach-Voice, die deine Flexpfad-Serie begleitet.",
  },
  {
    icon: MessageCircle,
    title: "Conversational Trigger",
    detail:
      "\u201eWie ist dein Gespr\u00e4ch gelaufen?\u201c, \u201eDiese Woche dein Stolperstein-Thema.\u201c \u2014 diese Nachfragen verstummen.",
  },
  {
    icon: Bell,
    title: "Wochen- + Monats-Rückblick",
    detail:
      "Montag-Wochen-Email + Monats-Rückblick zum 1. — beide stoppen sofort.",
  },
];

export default function CancellationScreen() {
  const navigate = useNavigate();
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pausing, setPausing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [pausedOk, setPausedOk] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/account/retention").then((r) => {
      setRetention(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handlePause = async () => {
    setError(null);
    setPausing(true);
    try {
      await api.post("/account/pause");
      setPausedOk(true);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "already_paused") {
        setPausedOk(true);
      } else {
        setError("Pause konnte nicht aktiviert werden. Bitte später erneut versuchen.");
      }
    } finally {
      setPausing(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    setError(null);
    try {
      // Sprint M5.56b (22.08.2026) — Cancel-Redirect zu Ablefy statt lokalem
      // DB-Flip. User kuendigt in Ablefy → Ablefy sendet
      // order.subscription.canceled-Webhook → F4S-Handler flippt DB.
      // Nur Free-User oder User ohne Ablefy-Abo fallen auf den lokalen
      // /account/cancel-Endpoint zurueck.
      const resp = await api.get("/account/manage-subscription-url");
      if (resp.data?.has_subscription && resp.data?.url) {
        const ok = window.confirm(
          `Du wirst zu deinem Ablefy-Käufer-Konto weitergeleitet. Wenn du kein Passwort hast, klicke einfach auf „Magic-Link anfordern“ – du bekommst einen Login-Link per E-Mail. Danach: „Meine Bestellungen“ → „Kündigen“. Deine Flexpfad-Bibliothek bleibt bis zum bezahlten Ende erhalten.`,
        );
        if (!ok) {
          setCancelling(false);
          return;
        }
        window.location.href = resp.data.url;
        return;
      }
      // Fallback: Free-User oder User ohne Abo → lokaler Soft-Cancel
      const ok = window.confirm(
        "Bist du sicher? Deine Flexpfad-Bibliothek wird in 14 Tagen anonymisiert. Du kannst innerhalb der 14 Tage reaktivieren.",
      );
      if (!ok) {
        setCancelling(false);
        return;
      }
      await api.post("/account/cancel");
      navigate("/cockpit");
    } catch (_e) {
      setError("Kündigung konnte nicht abgeschlossen werden. Bitte später erneut versuchen.");
      setCancelling(false);
    }
  };

  return (
    <RedesignLayout>
      {() => (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-24" data-testid="cancellation-screen">
          <button
            type="button"
            onClick={() => navigate("/dashboard/abo")}
            className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--fts-text-muted)] hover:text-[var(--fts-primary-bright)] mb-6"
            data-testid="cancel-back"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zu deinem Plan
          </button>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="cancel-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Lade…
            </div>
          ) : pausedOk ? (
            <motion.section
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              data-testid="cancel-pause-success"
              className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8 shadow-sm"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-emerald-900">
                    Pause aktiviert.
                  </h1>
                  <p className="mt-2 text-sm text-emerald-900/80 leading-relaxed">
                    Für die nächsten 30 Tage stoppen Wochen-Email, Monats-Email und alle
                    Conversational-Trigger. Deine Flexpfad-Bibliothek bleibt voll zugänglich —
                    du kannst jederzeit weitere Flexpfade anlegen.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/cockpit")}
                    data-testid="cancel-pause-back-to-cockpit"
                    className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-emerald-700"
                  >
                    Zurück zu deiner Flexpfad-Bibliothek
                  </button>
                </div>
              </div>
            </motion.section>
          ) : (
            <>
              <header className="mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-red-700 text-xs font-bold uppercase tracking-wider" data-testid="cancel-eyebrow">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Plan pausieren oder beenden
                </div>
                <h1
                  data-testid="cancel-headline"
                  className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--fts-text-primary)]"
                >
                  Was du verlierst, wenn du gehst.
                </h1>
                <p className="mt-2 text-base text-slate-600 leading-relaxed">
                  Wir bewahren deine Flexpfad-Bibliothek noch <strong>14 Tage</strong> auf —
                  danach wird es DSGVO-konform anonymisiert. Sechs Dinge stoppen
                  sofort mit deiner Kündigung:
                </p>
              </header>

              <ul
                data-testid="cancel-loss-list"
                className="space-y-3"
              >
                {LOSS_ITEMS.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.li
                      key={item.title}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                      data-testid={`cancel-loss-item-${i}`}
                      className="flex gap-3 p-4 rounded-xl border border-slate-200 bg-white"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[var(--fts-text-primary)] flex items-center gap-1.5">
                          <Lock className="w-3 h-3 text-slate-400" />
                          {item.title}
                        </p>
                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                          {item.detail}
                        </p>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>

              {/* PAUSE ANGEBOT */}
              <motion.section
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                data-testid="cancel-pause-offer"
                className="mt-8 rounded-2xl border-2 border-[#4563BB] bg-[#4563BB]/5 p-5 sm:p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#4563BB] flex items-center justify-center flex-shrink-0">
                    <Pause className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#4563BB]">
                      Stattdessen
                    </p>
                    <h2 className="mt-1 text-lg sm:text-xl font-extrabold text-[var(--fts-text-primary)]">
                      30 Tage Pause — ohne etwas zu verlieren.
                    </h2>
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                      Die nächsten 30 Tage keine Emails, keine Trigger — aber
                      deine Flexpfad-Bibliothek, deine Flexpfad-Reise und alle Hot Tips bleiben
                      komplett erhalten. Du kannst jederzeit weitere Flexpfade anlegen,
                      ohne Druck.
                    </p>
                    <button
                      type="button"
                      onClick={handlePause}
                      disabled={pausing}
                      data-testid="cancel-pause-btn"
                      className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#4563BB] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#3a55a3] disabled:opacity-50"
                    >
                      {pausing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Pause wird aktiviert…
                        </>
                      ) : (
                        <>
                          <Pause className="w-4 h-4" /> 30 Tage Pause aktivieren
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.section>

              {error && (
                <p data-testid="cancel-error" className="mt-4 text-sm text-red-600">
                  {error}
                </p>
              )}

              {/* KÜNDIGEN BESTÄTIGEN */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-between border-t border-slate-200 pt-6">
                <p className="text-xs text-slate-500" data-testid="cancel-ablefy-hint">
                  Deine Kündigung wird über dein Ablefy-Käufer-Konto abgewickelt (dort verwaltest du deine Zahlungen). Die Flexpfad-Bibliothek wartet 14 Tage auf dich.
                </p>
                <button
                  type="button"
                  onClick={handleConfirmCancel}
                  disabled={cancelling || retention?.state?.cancelled}
                  data-testid="cancel-confirm-btn"
                  className="inline-flex items-center gap-2 rounded-xl border border-red-300 bg-white text-red-700 px-5 py-2.5 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
                >
                  {cancelling ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Kündigung läuft…
                    </>
                  ) : (
                    "Trotzdem kündigen"
                  )}
                </button>
              </div>

              {/* Sprint P0-Hotfix (25.08.2026) — UX-Hinweis bei bereits gecanceltem User.
                  Verhindert Verwirrung ueber disabled Button (siehe Prod-Live-Test-Feedback). */}
              {retention?.state?.cancelled && (
                <div
                  data-testid="cancel-already-cancelled-hint"
                  className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                >
                  <p className="font-semibold">Dein Abo ist bereits gekündigt.</p>
                  <p className="mt-1">
                    Wenn du weiter Zugang möchtest, kannst du es{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/cockpit")}
                      data-testid="cancel-goto-cockpit-reactivate"
                      className="font-bold underline hover:no-underline"
                    >
                      im Mitgliederbereich reaktivieren
                    </button>
                    .
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </RedesignLayout>
  );
}
